import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  TaskMaterialWarehouseInput,
  TaskMaterialPlannedInput,
  TaskMaterialUpdateInput,
  TaskMaterialReturnInput,
} from '@models/warehouse.model';
import { warehouseService } from '@services/warehouse';
import { TASK_MATERIALS_QUERY_KEY } from './useTaskMaterials';
import { STOCK_LEVELS_QUERY_KEY } from './useStockLevels';
import { LOW_STOCK_ITEMS_QUERY_KEY } from './useLowStockItems';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

/** Extract a user-friendly error message from graphql-request errors */

export const useCreateTaskMaterialFromWarehouse = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'createFromWarehouse'],
    mutationFn: async (input: TaskMaterialWarehouseInput) => {
      return await warehouseService.createTaskMaterialFromWarehouse(input);
    },
    onSuccess: (_data, variables) => {
      toast.success(tActionMessages.materialAddedFromWarehouse);
      queryClient.invalidateQueries({
        queryKey: [...TASK_MATERIALS_QUERY_KEY, variables.taskId],
      });
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LOW_STOCK_ITEMS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(
        getErrorMessage(error, 'Failed to add material from warehouse'),
      );
    },
  });
};

export const useCreateTaskMaterialPlanned = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'createPlanned'],
    mutationFn: async (input: TaskMaterialPlannedInput) => {
      return await warehouseService.createTaskMaterialPlanned(input);
    },
    onSuccess: (_data, variables) => {
      toast.success(tActionMessages.plannedMaterialAdded);
      queryClient.invalidateQueries({
        queryKey: [...TASK_MATERIALS_QUERY_KEY, variables.taskId],
      });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to add planned material'));
    },
  });
};

export const useUpdateTaskMaterial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'update'],
    mutationFn: async (input: TaskMaterialUpdateInput) => {
      return await warehouseService.updateTaskMaterial(input);
    },
    onSuccess: () => {
      toast.success(tActionMessages.materialUpdated);
      queryClient.invalidateQueries({ queryKey: TASK_MATERIALS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update material'));
    },
  });
};

export const useReturnTaskMaterial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'return'],
    mutationFn: async (input: TaskMaterialReturnInput) => {
      return await warehouseService.returnTaskMaterial(input);
    },
    onSuccess: () => {
      toast.success(tActionMessages.materialReturnedToWarehouse);
      queryClient.invalidateQueries({ queryKey: TASK_MATERIALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LOW_STOCK_ITEMS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to return material'));
    },
  });
};

export const useDeleteTaskMaterial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'delete'],
    mutationFn: async (taskMaterialId: string) => {
      return await warehouseService.deleteTaskMaterial(taskMaterialId);
    },
    onSuccess: () => {
      toast.success(tActionMessages.materialRemoved);
      queryClient.invalidateQueries({ queryKey: TASK_MATERIALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LOW_STOCK_ITEMS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to remove material'));
    },
  });
};

export const useReserveTaskMaterial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'reserve'],
    mutationFn: async (taskMaterialId: string) => {
      return await warehouseService.reserveTaskMaterial(taskMaterialId);
    },
    onSuccess: () => {
      toast.success(tActionMessages.materialReservedFromWarehouse);
      queryClient.invalidateQueries({ queryKey: TASK_MATERIALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LOW_STOCK_ITEMS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to reserve material'));
    },
  });
};

export const useConsumeTaskMaterial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskMaterial', 'consume'],
    mutationFn: async (taskMaterialId: string) => {
      return await warehouseService.consumeTaskMaterial(taskMaterialId);
    },
    onSuccess: () => {
      toast.success(tActionMessages.materialMarkedAsConsumed);
      queryClient.invalidateQueries({ queryKey: TASK_MATERIALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LOW_STOCK_ITEMS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to consume material'));
    },
  });
};
